import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Box, Paper, Button, Divider, List, ListItem, ListItemText, Chip } from '@mui/material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import FeedbackButtons from 'components/FeedbackButtons';

const ICPProfile = ({ icpData, isProUser, handleTrialClick }) => {
  const [hoveredItems, setHoveredItems] = useState({});
  const taskId = useSelector((state) => state.currentTaskReducer);

  // Add safety check for entire icpData
  if (!icpData) return null;

  // Pre-transform business objectives data
  const transformedBusinessObjectives = icpData?.goalsAndObjectives?.businessObjectives
    ? Object.entries(icpData.goalsAndObjectives.businessObjectives).map(
        ([key, value]) =>
          `${
            key.charAt(0).toUpperCase() +
            key
              .slice(1)
              .replace(/([A-Z])/g, ' $1')
              .trim()
          }: ${value}`
      )
    : [];

  // const show_keys = ["age", "name", "gender", "location", "education", "familyStatus"];
  // console.log(icpData);
  const renderLockedContent = (content) => {
    try {
      // If content is an object, convert it to string representation
      const displayContent = typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content);

      return (
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Typography sx={{ filter: 'blur(5px)', userSelect: 'none', opacity: 0.45 }}>{displayContent}</Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              opacity: 0,
              transition: 'opacity 0.3s',
              '&:hover': {
                opacity: 1
              },
              cursor: 'pointer'
            }}
            onClick={handleTrialClick}
          >
            <LockOutlined />
            <Typography variant="caption" color="primary" ml={1}>
              Unlock
            </Typography>
          </Box>
        </Box>
      );
    } catch (error) {
      return null;
    }
  };

  const renderFreeUserContent = () => (
    <Box>
      <Paper elevation={1} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {icpData?.name}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          {icpData?.age} • {icpData?.gender} • {icpData?.location}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {icpData?.education} • {icpData?.familyStatus}
        </Typography>
        {renderSection('Professional Summary', icpData?.professionalSummary)}

        {renderSection(
          'Professional Background',
          icpData?.professionalBackground && (
            <>
              <Typography variant="body1" paragraph>
                <strong>Industry:</strong> {icpData.professionalBackground?.industry}
                <br />
                <strong>Job Title:</strong> {icpData.professionalBackground?.jobTitle}
                <br />
                <strong>Experience:</strong> {icpData.professionalBackground?.experience}
                <br />
                <strong>Company Size:</strong> {icpData.professionalBackground?.companySize}
              </Typography>
              <Typography variant="subtitle1">Key Responsibilities:</Typography>
              {renderLockedContent(icpData.professionalBackground?.keyResponsibilities)}
            </>
          )
        )}

        {renderSection(
          'Goals and Objectives',
          icpData?.goalsAndObjectives && (
            <>
              <Typography variant="body1" paragraph>
                <strong>Primary Goal:</strong> {renderLockedContent(icpData.goalsAndObjectives?.primaryGoals)}
              </Typography>
              <Typography variant="subtitle1">Success Metrics:</Typography>
              {renderLockedContent(icpData.goalsAndObjectives?.successMetrics)}
              <Typography variant="subtitle1" mt={2}>
                Business Objectives:
              </Typography>
              {renderLockedContent(icpData.goalsAndObjectives?.businessObjectives)}
            </>
          )
        )}

        {renderSection(
          'Challenges and Pain Points',
          icpData?.challengesAndPainPoints && (
            <>
              <Typography variant="subtitle1" mb={1}>
                Needs:
              </Typography>
              {renderLockedContent(icpData.challengesAndPainPoints?.needs)}
              <Typography variant="subtitle1" mt={2}>
                Obstacles:
              </Typography>
              {renderLockedContent(icpData.challengesAndPainPoints?.obstacles)}
              <Typography variant="subtitle1" mt={2}>
                Pain Points:
              </Typography>
              {renderLockedContent(icpData.challengesAndPainPoints?.painPoints)}
            </>
          )
        )}

        {renderSection(
          'Values and Motivations',
          icpData?.valuesAndMotivations && (
            <>
              <Typography variant="subtitle1" mb={1}>
                Core Values:
              </Typography>
              {renderLockedContent(icpData.valuesAndMotivations?.coreValues)}
              <Typography variant="subtitle1" mt={2}>
                Motivators:
              </Typography>
              {renderLockedContent(icpData.valuesAndMotivations?.motivators)}
              <Typography variant="subtitle1" mt={2}>
                Aspirations:
              </Typography>
              {renderLockedContent(icpData.valuesAndMotivations?.aspirations)}
            </>
          )
        )}

        {renderSection(
          'Professional Aspirations',
          icpData?.professionalAspirations && renderLockedContent(icpData.professionalAspirations)
        )}

        {renderSection(
          'Decision-Making Process',
          icpData?.objectionsAndDecisionCriteria && (
            <>
              <Typography variant="subtitle1">Influencers:</Typography>
              {renderLockedContent(icpData.objectionsAndDecisionCriteria?.influencers)}
              <Typography variant="subtitle1" mt={2}>
                Decision Factors:
              </Typography>
              {renderLockedContent(icpData.objectionsAndDecisionCriteria?.decisionFactors)}
              <Typography variant="subtitle1" mt={2}>
                Common Objections:
              </Typography>
              {renderLockedContent(icpData.objectionsAndDecisionCriteria?.commonObjections)}
            </>
          )
        )}

        {renderSection(
          'Preferred Communication Channels',
          icpData?.preferredCommunicationChannels && (
            <>
              <Typography variant="subtitle1">Channels:</Typography>
              {renderLockedContent(icpData.preferredCommunicationChannels?.channels)}
              <Typography variant="subtitle1" mt={2}>
                Content Preferences:
              </Typography>
              {renderLockedContent(icpData.preferredCommunicationChannels?.contentPreferences)}
            </>
          )
        )}

        {renderSection(
          'Behaviors and Habits',
          icpData?.behaviorsAndHabits && (
            <>
              <Typography variant="subtitle1">Buying Behavior:</Typography>
              {renderLockedContent(icpData.behaviorsAndHabits?.buyingBehavior)}
              <Typography variant="subtitle1" mt={2}>
                Technology Usage:
              </Typography>
              {renderLockedContent(icpData.behaviorsAndHabits?.technologyUsage)}
              <Typography variant="subtitle1" mt={2}>
                Media Consumption:
              </Typography>
              {renderLockedContent(icpData.behaviorsAndHabits?.mediaConsumption)}
            </>
          )
        )}

        <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
          <Typography variant="h5" color="primary" mb={2}>
            Start your FREE 7 days trial to access the full Ideal Customer Profile!
          </Typography>
          <Button variant="contained" color="primary" onClick={handleTrialClick}>
            Unlock ICP Details
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  const renderSection = (title, content) => {
    if (!content) return null;
    const itemKey = title.replace(/\s+/g, '-').toLowerCase();

    // FIX: Check if content is a React element to avoid circular JSON error.
    const blockContentForFeedback = React.isValidElement(content)
      ? `[component content for: ${title}]`
      : typeof content === 'object'
      ? JSON.stringify(content)
      : String(content);

    return (
      <Box
        mt={4}
        onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: true }))}
        onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: false }))}
      >
        <Typography variant="h5" color="primary" gutterBottom id={`icp-${title.toLowerCase().replace(/\s+/g, '_')}`}>
          {title}
        </Typography>
        {/* FIX: Use a Box to render content that might be a component, instead of Typography which expects a string */}
        <Box>{content}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <FeedbackButtons
            blockId={itemKey}
            blockType="icp-section"
            blockTitle={title}
            blockContent={blockContentForFeedback}
            taskId={taskId}
            serviceName={icpData?.name}
            isVisible={!!hoveredItems[itemKey]}
          />
        </Box>
      </Box>
    );
  };

  const renderList = (items) => {
    try {
      // Handle case where items is not an array
      if (!Array.isArray(items)) {
        // If it's a string, just display it as text
        if (typeof items === 'string') {
          return (
            <Typography variant="body1" sx={{ pl: 0, lineHeight: 1.5 }}>
              {items}
            </Typography>
          );
        }
        // If it's not an array or string, return null
        return null;
      }

      if (!items?.length) return null;

      return (
        <List sx={{ pl: 0, '& .MuiListItem-root': {} }}>
          {items.map((item, index) => (
            <ListItem key={index} disablePadding>
              <ListItemText
                primary={`• ${item}`}
                primaryTypographyProps={{
                  variant: 'body1',
                  sx: { lineHeight: 1.5 }
                }}
              />
            </ListItem>
          ))}
        </List>
      );
    } catch (error) {
      return null;
    }
  };

  const renderChips = (items) => {
    // Handle case where items is not an array
    if (!Array.isArray(items)) {
      // If it's a string, just display it as text
      if (typeof items === 'string') {
        return (
          <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
            {items}
          </Typography>
        );
      }
      // If it's not an array or string, return null
      return null;
    }

    if (!items?.length) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
        {items.map((item, index) => (
          <Chip
            key={index}
            label={item}
            variant="outlined"
            sx={{
              px: 1,
              py: 1.5,
              color: 'grey.800',
              borderRadius: '4px',
              fontSize: '0.75rem',
              border: '1px solid',
              borderColor: 'grey.300'
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box mt={4}>
      {isProUser ? (
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {icpData?.name}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            {icpData?.age} • {icpData?.gender} • {icpData?.location}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {icpData?.education} • {icpData?.familyStatus}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {renderSection('Professional Summary', icpData?.professionalSummary)}

          {renderSection(
            'Professional Background',
            icpData?.professionalBackground && (
              <Typography variant="body1" paragraph>
                <strong>Industry:</strong> {icpData.professionalBackground.industry}
                <br />
                <strong>Company:</strong> {icpData.professionalBackground.company} {icpData.professionalBackground.companySize}
                <br />
                <strong>Job Title:</strong> {icpData.professionalBackground.jobTitle}
                <br />
                <strong>Experience:</strong> {icpData.professionalBackground.experience}
              </Typography>
            )
          )}

          {renderSection(
            'Key Responsibilities',
            icpData?.professionalBackground?.keyResponsibilities && renderList(icpData.professionalBackground.keyResponsibilities)
          )}

          {renderSection(
            'Goals and Objectives',
            icpData?.goalsAndObjectives && (
              <>
                <Typography variant="body1" paragraph>
                  <strong>Primary Goal:</strong> {icpData.goalsAndObjectives?.primaryGoals}
                </Typography>
                <Typography variant="subtitle1">Success Metrics:</Typography>
                {renderList(icpData.goalsAndObjectives?.successMetrics)}
                <Typography variant="subtitle1" mt={2}>
                  Business Objectives:
                </Typography>
                {renderList(transformedBusinessObjectives)}
              </>
            )
          )}

          {renderSection(
            'Challenges and Pain Points',
            icpData?.challengesAndPainPoints && (
              <>
                <Typography variant="subtitle1" mb={1}>
                  Needs:
                </Typography>
                {renderList(icpData.challengesAndPainPoints.needs)}
                <Typography variant="subtitle1" mt={2}>
                  Obstacles:
                </Typography>
                {renderList(icpData.challengesAndPainPoints.obstacles)}
                <Typography variant="subtitle1" mt={2}>
                  Pain Points:
                </Typography>
                {renderList(icpData.challengesAndPainPoints.painPoints)}
              </>
            )
          )}

          {renderSection(
            'Values and Motivations',
            icpData?.valuesAndMotivations && (
              <>
                <Typography variant="subtitle1" mb={1}>
                  Core Values:
                </Typography>
                {renderChips(icpData.valuesAndMotivations?.coreValues)}
                <Typography variant="subtitle1" mt={2}>
                  Motivators:
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {icpData.valuesAndMotivations?.motivators}
                </Typography>
                <Typography variant="subtitle1" mt={2}>
                  Aspirations:
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {/* {icpData.valuesAndMotivations.aspirations} */}
                  {icpData.professionalAspirations}
                </Typography>
              </>
            )
          )}

          {renderSection(
            'Decision-Making Process',
            icpData?.objectionsAndDecisionCriteria && (
              <>
                <Typography variant="subtitle1">Decision Factors:</Typography>
                {renderList(icpData.objectionsAndDecisionCriteria?.decisionFactors)}
                <Typography variant="subtitle1" mt={2}>
                  Common Objections:
                </Typography>
                {renderList(icpData.objectionsAndDecisionCriteria?.commonObjections)}
                <Typography variant="subtitle1" mt={2}>
                  Influencers:
                </Typography>
                {renderChips(icpData.objectionsAndDecisionCriteria?.influencers)}
              </>
            )
          )}

          {renderSection(
            'Preferred Communication Channels',
            icpData?.preferredCommunicationChannels && (
              <>
                <Typography variant="subtitle1">Channels:</Typography>
                {renderChips(icpData.preferredCommunicationChannels?.channels)}
                <Typography variant="subtitle1" mt={2}>
                  Content Preferences:
                </Typography>
                {renderChips(icpData.preferredCommunicationChannels?.contentPreferences)}
              </>
            )
          )}

          {renderSection(
            'Behaviors and Habits',
            icpData?.behaviorsAndHabits && (
              <>
                <Typography variant="subtitle1">Buying Behavior:</Typography>
                <Typography variant="body1">{icpData.behaviorsAndHabits?.buyingBehavior}</Typography>
                <Typography variant="subtitle1" mt={2}>
                  Technology Usage:
                </Typography>
                {Object.entries(icpData.behaviorsAndHabits?.technologyUsage || {}).map(([category, tools]) => (
                  <Box key={category} mt={1}>
                    <Typography variant="body2">
                      <strong>
                        {category.charAt(0).toUpperCase() +
                          category
                            .slice(1)
                            .replace(/([A-Z])/g, ' $1')
                            .trim()}
                        :
                      </strong>
                    </Typography>
                    {renderChips(Array.isArray(tools) ? tools : [])}
                  </Box>
                ))}
                <Typography variant="subtitle1" mt={2}>
                  Media Consumption:
                </Typography>
                {renderChips(
                  Array.isArray(icpData.behaviorsAndHabits?.mediaConsumption) ? icpData.behaviorsAndHabits.mediaConsumption : []
                )}
              </>
            )
          )}
        </Paper>
      ) : (
        renderFreeUserContent()
      )}
    </Box>
  );
};

export default ICPProfile;
